if (!customElements.get('tab-component')) {
  class TabComponent extends HTMLElement {
    // ── Private state ──────────────────────────────────────────────────────────
    #tabs          = [];
    #panels        = [];
    #handlers      = new Map(); // tab el → bound click handler
    #observer      = null;
    #navEl         = null;
    #navKeyHandler = null;

    // ── Lifecycle ──────────────────────────────────────────────────────────────
    connectedCallback() {
      // Defer so that child nodes are fully parsed when the element is stamped
      // as part of a Liquid / innerHTML injection rather than a true custom element.
      if (this.childElementCount === 0) {
        requestAnimationFrame(() => this.#init());
      } else {
        this.#init();
      }
    }

    disconnectedCallback() {
      this.#teardown();
    }

    // ── Init / teardown ────────────────────────────────────────────────────────
    #init() {
      this.#tabs   = Array.from(this.querySelectorAll('.tab-component__tab'));
      this.#panels = Array.from(this.querySelectorAll('.tab-component__panel'));

      if (this.#tabs.length === 0) return;

      // Click handlers
      this.#tabs.forEach((tab) => {
        const handler = () => {
          const index = parseInt(tab.getAttribute('data-tab-index'), 10);
          if (!Number.isNaN(index)) this.activate(index);
        };
        this.#handlers.set(tab, handler);
        tab.addEventListener('click', handler);
      });

      // Keyboard navigation — ARIA APG Tabs pattern (roving tabindex, auto-activate)
      this.#navEl = this.querySelector('.tab-component__nav');
      if (this.#navEl) {
        this.#navKeyHandler = (e) => {
          const count   = this.#tabs.length;
          const current = this.#tabs.indexOf(document.activeElement);
          if (current === -1) return;

          let next = -1;
          if (e.key === 'ArrowRight' || e.key === 'ArrowDown') next = (current + 1) % count;
          if (e.key === 'ArrowLeft'  || e.key === 'ArrowUp')   next = (current - 1 + count) % count;
          if (e.key === 'Home') next = 0;
          if (e.key === 'End')  next = count - 1;

          if (next >= 0) {
            e.preventDefault();
            this.activate(next);
            this.#tabs[next].focus();
          }
        };
        this.#navEl.addEventListener('keydown', this.#navKeyHandler);
      }

      // Watch for DOM mutations so that dynamically injected tabs/panels
      // (e.g. Shopify section re-renders) are handled without a full page reload.
      this.#observer = new MutationObserver(() => {
        this.#teardown();
        this.#init();
      });
      this.#observer.observe(this, { childList: true, subtree: false });

      // Activate the first tab by default unless one is already marked active.
      const activeIndex = this.#tabs.findIndex((t) =>
        t.classList.contains('tab-component__tab--active')
      );
      this.activate(activeIndex >= 0 ? activeIndex : 0);
    }

    #teardown() {
      this.#observer?.disconnect();
      this.#observer = null;

      if (this.#navEl && this.#navKeyHandler) {
        this.#navEl.removeEventListener('keydown', this.#navKeyHandler);
      }
      this.#navEl         = null;
      this.#navKeyHandler = null;

      this.#handlers.forEach((handler, tab) => {
        tab.removeEventListener('click', handler);
      });
      this.#handlers.clear();

      this.#tabs   = [];
      this.#panels = [];
    }

    // ── Public API ─────────────────────────────────────────────────────────────
    activate(index) {
      if (index < 0 || index >= this.#tabs.length) return;

      this.#tabs.forEach((tab, i) => {
        const isActive = i === index;
        tab.classList.toggle('tab-component__tab--active', isActive);
        tab.setAttribute('aria-selected', String(isActive));
        tab.setAttribute('tabindex', isActive ? '0' : '-1');
      });

      this.#panels.forEach((panel, i) => {
        const isActive = i === index;
        panel.classList.toggle('tab-component__panel--active', isActive);
        panel.hidden = !isActive;
      });

      this.dispatchEvent(
        new CustomEvent('tab-component:change', {
          bubbles: true,
          composed: true,
          detail: { index },
        })
      );
    }
  }

  customElements.define('tab-component', TabComponent);
}
